import { Injectable } from '@angular/core';
import { BehaviorSubject, filter } from 'rxjs';
import { ILocationOption } from '@models/auth/auth.model';
import { UserType } from '@core/enums/type.enum';
import { LookupService } from '@services/master/lookup.service';
import { LookupEntity } from '@core/constants/lookup.const';

@Injectable({ providedIn: 'root' })
export class LocationFilterService {
  private _availableLocations$ = new BehaviorSubject<ILocationOption[]>([]);
  private _selectedLocationIds$ = new BehaviorSubject<string[]>([]);
  private _userTypeId: number | null = null;
  private _initialized = false;
  private _isInitializing = false;

  availableLocations$ = this._availableLocations$.asObservable();
  locationFilter$ = this._selectedLocationIds$.asObservable().pipe(
    filter(() => this._initialized)
  );

  constructor(private lookupService: LookupService) {}

  initialize(locations: ILocationOption[], userTypeId: number): void {
    if (this._initialized || this._isInitializing) {
      return;
    }
    this._userTypeId = userTypeId;
    
    if (!locations || locations.length === 0) {
      this._isInitializing = true;
      this.lookupService.getLookup({ entityName: LookupEntity.Location }).subscribe({
        next: (res: any) => {
          const allLocations = res?.data?.map((l: any) => ({ id: l.idEnc, name: l.name })) || [];
          const defaultSelectedIds = this.getDefaultSelectedIds(allLocations, userTypeId);
          this._initialized = true;
          this._isInitializing = false;
          this.setLocations(defaultSelectedIds);
          this.setAvailableLocations(allLocations);
        },
        error: () => {
          this._isInitializing = false;
        }
      });
    } else {
      const defaultSelectedIds = this.getDefaultSelectedIds(locations, userTypeId);
      this._initialized = true;
      this.setLocations(defaultSelectedIds);
      this.setAvailableLocations(locations);
    }
  }

  isInitialized(): boolean {
    return this._initialized;
  }

  setLocations(ids: string[]): void {
    const next = ids ?? [];
    const current = this._selectedLocationIds$.getValue();
    if (this.areIdsEqual(current, next)) return;
    this._selectedLocationIds$.next(next);
  }

  getLocations(): string[] {
    return this._selectedLocationIds$.getValue();
  }

  getAvailableLocations(): ILocationOption[] {
    return this._availableLocations$.getValue();
  }

  getLocationsAsString(): string {
    return this._selectedLocationIds$.getValue().join(',');
  }

  /**
   * Returns clean, comma-separated selected location IDs string.
   * Extracts clean IDs from the provided form control value (ignoring '0', empty/null values).
   * If formControlValue is empty, null, undefined, or contains only '0',
   * falls back to the current active header locations in LocationFilterService.
   */
  getSelectedLocationIds(formControlValue?: any): string {
    if (formControlValue !== undefined && formControlValue !== null && formControlValue !== '') {
      const rawArr = Array.isArray(formControlValue) ? formControlValue : [formControlValue];
      const cleanIds = rawArr
        .filter((id: any) => id !== null && id !== undefined && id !== '' && id !== '0' && id !== 0)
        .map((id: any) => String(id).trim())
        .filter(Boolean);

      if (cleanIds.length > 0) {
        return cleanIds.join(',');
      }
    }
    return this.getLocationsAsString();
  }

  /**
   * Returns clean array of selected location IDs.
   */
  getSelectedLocationIdsAsArray(formControlValue?: any): string[] {
    const idsString = this.getSelectedLocationIds(formControlValue);
    return idsString ? idsString.split(',').filter(Boolean) : [];
  }

  refreshLocations(locationIds: string, resolvedOptions?: ILocationOption[]): void {
    const ids = locationIds
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    const available = this.buildAvailableLocations(ids, resolvedOptions);
    this.setLocations(
      this.getDefaultSelectedIds(available, this._userTypeId ?? UserType.Admin)
    );
    this.setAvailableLocations(available);
  }

  clear(): void {
    this._userTypeId = null;
    this._initialized = false;
    this._isInitializing = false;
  }

  private setAvailableLocations(locations: ILocationOption[]): void {
    const next = locations ?? [];
    const current = this._availableLocations$.getValue();
    if (this.areLocationsEqual(current, next)) return;
    this._availableLocations$.next(next);
  }

  private areIdsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((id, index) => id === b[index]);
  }

  private areLocationsEqual(a: ILocationOption[], b: ILocationOption[]): boolean {
    if (a.length !== b.length) return false;
    return a.every(
      (loc, index) => loc.id === b[index]?.id && loc.name === b[index]?.name
    );
  }

  private getDefaultSelectedIds(
    locations: ILocationOption[],
    userTypeId: number
  ): string[] {
    if (!locations.length) return [];

    if (userTypeId === UserType.Receptionist) {
      return [locations[0].id];
    }

    return locations.map((loc) => loc.id);
  }

  private buildAvailableLocations(
    ids: string[],
    resolvedOptions?: ILocationOption[]
  ): ILocationOption[] {
    const currentAvailable = this._availableLocations$.getValue();
    const resolvedById = new Map(
      (resolvedOptions ?? []).map((loc) => [loc.id, loc])
    );

    return ids.map((id) => {
      const resolved = resolvedById.get(id);
      if (resolved) return resolved;

      const existing = currentAvailable.find((loc) => loc.id === id);
      return existing ?? { id, name: '' };
    }).filter((loc) => loc.name);
  }
}
